import { uniq } from "es-toolkit/array";
import type { VizDataset, VizField } from "@/lib/ai/visualization/data/schema";

function findField(dataset: VizDataset, key: string): VizField | null {
  return dataset.fields.find((field) => field.name === key) ?? null;
}

/**
 * 指定 field の metadata label を返す。
 *
 * @remarks
 * chart の user-facing label は LLM authoring が責務を持つため、
 * この helper は table や internal metadata hint のような deterministic 用途だけに使う。
 */
function resolveFieldLabel(
  dataset: VizDataset,
  key: string | null | undefined,
): string | null {
  if (typeof key !== "string" || key.length === 0) return null;
  const field = findField(dataset, key);
  return field?.label ?? field?.name ?? null;
}

/**
 * field key 配列から key->label map を構築する。
 *
 * @remarks
 * chart legend の既定値ではなく、table や debug surface 向けの metadata map を返す。
 */
export function buildFieldLabelMap(
  dataset: VizDataset,
  keys: readonly string[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const key of uniq(keys)) {
    const label = resolveFieldLabel(dataset, key);
    if (typeof label === "string" && label.length > 0) {
      map[key] = label;
    }
  }
  return map;
}

function isRankField(field: VizField | null): boolean {
  return (
    field?.unit === "rank" ||
    (field?.aggregation?.kind === "non-aggregatable" &&
      field.aggregation.reason === "rank")
  );
}

/**
 * rank-only の time-series に既定の軸向きを与える。
 *
 * @remarks
 * Search Analytics の avg. position のような lower-is-better 指標は、
 * authoring payload が presentation prop を持たなくても compile 時点で
 * 反転軸へ寄せておくと、source UI と同じ読み方向を保ちやすい。
 */
export function resolveRankOnlyAxisDirections(
  dataset: VizDataset,
  metricKeys: readonly string[],
): { primary: "reversed"; secondary: "normal" } | null {
  const uniqueMetricKeys = uniq(
    metricKeys.filter(
      (key): key is string => typeof key === "string" && key.length > 0,
    ),
  );
  if (uniqueMetricKeys.length === 0) {
    return null;
  }

  const fields = uniqueMetricKeys.map((key) => findField(dataset, key));
  if (fields.every((field) => isRankField(field))) {
    return { primary: "reversed", secondary: "normal" };
  }
  return null;
}

/**
 * single-metric ranking chart の既定 sort を metric semantics から決める。
 *
 * @remarks
 * lower-is-better 指標では `desc` を固定すると source use case と逆方向に
 * 読まれやすいため、authoring が sort を省略したときだけ rank-aware default を与える。
 */
export function resolvePreferredRankingSort(args: {
  dataset: VizDataset;
  valueKey: string;
  requestedSort?: "asc" | "desc" | "none";
}): "asc" | "desc" | "none" {
  if (
    args.requestedSort === "asc" ||
    args.requestedSort === "desc" ||
    args.requestedSort === "none"
  ) {
    return args.requestedSort;
  }

  return isRankField(findField(args.dataset, args.valueKey)) ? "asc" : "desc";
}

/**
 * table 既定列を dataset metadata から決める。
 *
 * @remarks
 * table authoring が列指定を省いた場合でも deterministic に再現できるよう、
 * defaults を優先し、無ければ field 定義順をそのまま使う。
 */
export function resolveDefaultTableColumns(dataset: VizDataset): string[] {
  const preferred = uniq(
    [
      dataset.defaults?.x,
      dataset.defaults?.category,
      dataset.defaults?.series,
      dataset.defaults?.value,
      ...(dataset.defaults?.y ?? []),
    ].filter((key): key is string => typeof key === "string" && key.length > 0),
  ).filter((key) => findField(dataset, key) !== null);

  if (preferred.length > 0) {
    return preferred;
  }
  return dataset.fields.map((field) => field.name);
}

/**
 * table の初期表示列を dataset の `defaultVisible` から絞り込む。
 *
 * @remarks
 * `defaultVisible` が付いていない dataset では全列を表示し、暗黙非表示は作らない。
 */
export function resolveDefaultVisibleColumns(
  dataset: VizDataset,
  columns: readonly string[],
): string[] {
  const visible = columns.filter((column) => {
    const field = findField(dataset, column);
    return field?.defaultVisible === true;
  });
  return visible.length > 0 ? visible : [...columns];
}
